import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output, forwardRef, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DxcSelectOption } from '../dxc.types';
import { DxcPanelAnchor } from '../panel/dxc-panel-anchor';

/**
 * Multiselección con fichas dentro del disparador.
 *
 * El panel no se cierra al elegir — se eligen varias — y por eso lleva un pie
 * con el conteo y la acción de limpiar. A partir de `maxChips` el disparador
 * colapsa el resto en `+N`: si crece sin límite, empuja el formulario entero
 * hacia abajo cada vez que el usuario elige algo.
 */
@Component({
    standalone: true,
    selector: 'dxc-multiselect',
    templateUrl: './dxc-multiselect.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DxcMultiselectComponent), multi: true }],
})
export class DxcMultiselectComponent implements ControlValueAccessor, OnDestroy {

    @Input() options: DxcSelectOption[] = [];
    @Input() placeholder = 'Selecciona una o varias';
    @Input() invalid = false;
    @Input() ariaLabel = '';
    @Input() clearable = true;
    /** Fichas visibles antes de colapsar en `+N`. */
    @Input() maxChips = 3;

    @HostBinding('class.dxc-combo') readonly hostClass = true;
    @HostBinding('class.is-open') get isOpen() { return this.open; }

    @Input() set value(value: string[]) { this.#value = value ?? []; }
    get value() { return this.#value; }
    @Output() valueChange = new EventEmitter<string[]>();

    #value: string[] = [];
    disabled = false;
    /**
     * Accesor y no campo llano: el panel es `fixed` y su ancla tiene que
     * engancharse y soltarse exactamente cuando se abre y se cierra. Con un campo
     * habría que recordarlo en los seis sitios que lo asignan.
     */
    #open = false;
    get open() { return this.#open; }
    set open(value: boolean) {
        if (this.#open === value) return;
        this.#open = value;
        value ? this.anchor.attach() : this.anchor.detach();
    }

    private anchor!: DxcPanelAnchor;
    get panelStyle() { return this.anchor.style; }
    get panelUp() { return this.anchor.up; }
    activeIndex = 0;

    #onChange: (value: string[]) => void = () => { };
    #onTouched: () => void = () => { };

    constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {
        this.anchor = new DxcPanelAnchor(
            this.host.nativeElement, '.dxc-combo__trigger', () => this.cdr.markForCheck());
    }

    ngOnDestroy() { this.anchor.detach(); }

    get selectedOptions() { return this.options.filter(option => this.#value.includes(option.value)); }
    get visibleChips() { return this.selectedOptions.slice(0, this.maxChips); }
    get hiddenCount() { return Math.max(0, this.selectedOptions.length - this.maxChips); }

    isSelected(value: string) { return this.#value.includes(value); }

    writeValue(value: string[]) { this.#value = value ?? []; this.cdr.markForCheck(); }
    registerOnChange(fn: (value: string[]) => void) { this.#onChange = fn; }
    registerOnTouched(fn: () => void) { this.#onTouched = fn; }
    setDisabledState(disabled: boolean) { this.disabled = disabled; if (disabled) this.open = false; this.cdr.markForCheck(); }

    onTriggerClick(event: Event) {
        const remove = (event.target as HTMLElement).closest('[data-rm]');
        if (remove) {
            event.stopPropagation();
            this.toggleOption(remove.getAttribute('data-rm')!);
            return;
        }
        if (this.disabled) return;
        this.open = !this.open;
        this.activeIndex = 0;
        if (!this.open) this.#onTouched();
        this.cdr.markForCheck();
    }

    toggleOption(value: string) {
        this.#value = this.#value.includes(value)
            ? this.#value.filter(item => item !== value)
            : [...this.#value, value];
        this.#emit();
    }

    clear() { this.#value = []; this.#emit(); }

    #emit() {
        this.#onChange(this.#value);
        this.valueChange.emit(this.#value);
        this.cdr.markForCheck();
    }

    onKeydown(event: KeyboardEvent) {
        switch (event.key) {
            case 'Escape': this.open = false; this.#onTouched(); break;
            case 'ArrowDown':
            case 'ArrowUp':
                if (!this.open) { this.open = true; this.activeIndex = 0; break; }
                this.activeIndex = Math.min(this.options.length - 1, Math.max(0, this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1)));
                break;
            case 'Enter':
            case ' ':
                if (!this.open) { this.open = true; this.activeIndex = 0; break; }
                // Alterna sin cerrar: es multiselección.
                if (this.options[this.activeIndex]) this.toggleOption(this.options[this.activeIndex].value);
                break;
            // Atajo estándar de los campos con fichas.
            case 'Backspace':
                if (!this.#value.length) return;
                this.#value = this.#value.slice(0, -1);
                this.#emit();
                break;
            default: return;
        }
        event.preventDefault();
        this.cdr.markForCheck();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentDown(event: Event) {
        if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
            this.open = false;
            this.#onTouched();
            this.cdr.markForCheck();
        }
    }
}
